import * as z from "zod";
import { userFacingError } from "../authorization";
import { zql } from "../generatedDrizzleSchema";
import {
  grantEntry,
  insertGrantsDeduped,
  validateGrantsInput,
} from "../rbac/validation";
import { skills as builtinSkills } from "../skills";
import { isSameGitSkillSource } from "../skills/gitSource";
import { skillNameError } from "../skills/spec";
import { clientGeneratableIdSchema } from "../id";
import { mutator } from "./mutator";

const installation = z.object({
  skillId: clientGeneratableIdSchema,
  sourcePath: z.string(),
  name: z.string().optional(),
});

export const installGitSkillsSchema = z.object({
  sourceId: z.string(),
  connectedAppId: z.string().nullable(),
  installations: z.array(installation).min(1),
  grants: z.array(grantEntry).optional(),
});

export const installGitSkills = mutator({
  schema: installGitSkillsSchema,
  resolve: {},
  permissions: [
    {
      permission: "tenant:skill_create",
      scopeId: ({ ctx }) => ctx.tenantId,
    },
    {
      permission: "connected_app:use",
      scopeId: ({ args }) => args.connectedAppId,
    },
  ],
  fn: async ({
    tx,
    ctx,
    args: { sourceId, connectedAppId, installations, grants },
  }) => {
    const [source] = await tx.run(
      zql.skillGitSources
        .where("id", sourceId)
        .where("createdByUserId", ctx.userId)
    );
    if (
      !source ||
      source.syncStartedAt !== null ||
      source.lastError ||
      !source.resolvedCommit
    ) {
      throw userFacingError("Git skill discovery is not ready");
    }
    if (source.connectedAppId !== connectedAppId) {
      throw userFacingError("Git connector does not match this source");
    }
    if (grants) validateGrantsInput(grants, "skill");

    const existing = await tx.run(zql.skills);
    const existingGitInstallations = await tx.run(
      zql.skillGitInstallations.related("source")
    );
    const existingNames = new Set([
      ...existing.map((skill) => skill.name),
      ...builtinSkills.map((skill) => skill.name),
    ]);
    const selectedNames = new Set<string>();
    const now = Date.now();

    for (const requested of installations) {
      const discovered = source.catalog.find(
        (entry) => entry.path === requested.sourcePath
      );
      if (
        !discovered?.name ||
        !discovered.description ||
        discovered.error !== null
      ) {
        throw userFacingError("Selected skill is not valid");
      }
      if (
        existingGitInstallations.some(
          (installation) =>
            installation.sourcePath === requested.sourcePath &&
            installation.source !== undefined &&
            isSameGitSkillSource(source, installation.source)
        )
      ) {
        throw userFacingError(
          `Skill “${discovered.name}” is already installed from this source`
        );
      }
      const name = (requested.name ?? discovered.name).trim();
      const nameError = skillNameError(name);
      if (nameError) {
        throw userFacingError(`Invalid skill name: ${nameError}`);
      }
      if (existingNames.has(name) || selectedNames.has(name)) {
        throw userFacingError(`Skill “${name}” already exists`);
      }
      selectedNames.add(name);
      await tx.mutate.skills.insert({
        id: requested.skillId,
        name,
        description: discovered.description,
        instructions: "",
        enabled: true,
        fresh: true,
        createdAt: now,
        createdByUserId: ctx.userId,
        tenantId: ctx.tenantId,
      });
      await tx.mutate.skillGitInstallations.insert({
        skillId: requested.skillId,
        sourceId,
        sourcePath: requested.sourcePath,
        lastSyncedCommit: null,
        syncStartedAt: now,
        lastSyncedAt: null,
        lastError: null,
        tenantId: ctx.tenantId,
      });
      if (grants) {
        await insertGrantsDeduped(tx, grants, requested.skillId, ctx.tenantId);
      }
    }
  },
});
